/**
 * AdminAIInsightsTab — Phase 3.
 *
 * Founder intelligence layer. Cards-first, glanceable. Heuristics-only;
 * every score traceable to its contributing factors so it's never a
 * black box.
 *
 * Sub-tabs:
 *   Overview · Retention · Conversion · Churn Risk · Feature Insights
 *   Experiment Insights · Recommendations · Anomalies
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Brain, RefreshCw, TrendingUp, TrendingDown, Minus,
  AlertTriangle, Sparkles, Crown, Target, Activity,
  Heart, FlaskConical, Lightbulb, Zap, ShieldAlert, Bell,
  ArrowRight, Lock, Send, MessageSquare, Mail, Gift,
  Clock, BarChart3,
} from "lucide-react";

// ── helpers ────────────────────────────────────────────────────────────

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const sec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

function TrendArrow({ trend, className = "" }) {
  if (trend === "up") return <TrendingUp className={`w-3.5 h-3.5 text-emerald-400 ${className}`} />;
  if (trend === "down") return <TrendingDown className={`w-3.5 h-3.5 text-red-400 ${className}`} />;
  return <Minus className={`w-3.5 h-3.5 text-muted-foreground ${className}`} />;
}

function SeverityBadge({ severity }) {
  const map = {
    critical: "bg-red-600/20 text-red-300 border-red-600/40",
    high: "bg-red-500/15 text-red-400 border-red-500/30",
    medium: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    normal: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    insufficient_data: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
    low: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
  };
  return (
    <Badge variant="outline" className={`uppercase text-[10px] ${map[severity] || ""}`}>
      {severity || "unknown"}
    </Badge>
  );
}

function ConfidenceBadge({ confidence }) {
  if (confidence === undefined || confidence === null) return null;
  const isStr = typeof confidence === "string";
  const label = isStr ? confidence
    : confidence >= 70 ? "high"
    : confidence >= 40 ? "medium" : "low";
  const tint = label === "high" ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"
    : label === "medium" ? "text-amber-400 border-amber-500/30 bg-amber-500/10"
    : "text-zinc-400 border-zinc-500/30 bg-zinc-500/10";
  return (
    <Badge variant="outline" className={`uppercase text-[10px] ${tint}`}>
      {label} confidence{!isStr ? ` · ${Math.round(confidence)}` : ""}
    </Badge>
  );
}

function ImpactBadge({ impact }) {
  const map = {
    revenue: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    retention: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    activation: "bg-pink-500/15 text-pink-400 border-pink-500/30",
  };
  return (
    <Badge variant="outline" className={`uppercase text-[10px] ${map[impact] || ""}`}>
      {impact}
    </Badge>
  );
}

function ScoreBar({ score, accent = "primary" }) {
  const tints = {
    primary: "bg-primary",
    blue: "bg-blue-500",
    pink: "bg-pink-500",
    red: "bg-red-500",
    amber: "bg-amber-400",
    emerald: "bg-emerald-500",
  };
  return (
    <div className="h-2 rounded-full bg-muted overflow-hidden">
      <div
        className={`h-full ${tints[accent] || tints.primary}`}
        style={{ width: `${Math.max(0, Math.min(100, score))}%` }}
      />
    </div>
  );
}

function KpiCard({ label, value, hint, icon, accent }) {
  const ring = accent === "red"
    ? "ring-1 ring-red-500/40 bg-red-500/5"
    : accent === "amber"
      ? "ring-1 ring-amber-500/40 bg-amber-500/5"
      : "";
  return (
    <Card className={ring}>
      <CardContent className="p-3 flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold tabular-nums">{value}</p>
          {hint && <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{hint}</p>}
        </div>
        {icon}
      </CardContent>
    </Card>
  );
}

function EmptyCard({ children }) {
  return (
    <Card>
      <CardContent className="py-8 text-center text-sm text-muted-foreground">
        {children}
      </CardContent>
    </Card>
  );
}

// ── overview ──────────────────────────────────────────────────────────

function OverviewPanel({ data }) {
  const head = data?.headline || {};
  const recs = data?.top_recommendations || [];
  const anomalies = data?.top_anomalies || [];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <KpiCard label="Users" value={head.users ?? 0}
                 icon={<Activity className="w-4 h-4 text-primary" />} />
        <KpiCard label="Premium %" value={`${head.premium_pct ?? 0}%`}
                 icon={<Crown className="w-4 h-4 text-amber-400" />} />
        <KpiCard label="Avg retention" value={head.avg_retention ?? 0}
                 hint="0–100"
                 icon={<Heart className="w-4 h-4 text-pink-400" />} />
        <KpiCard label="At risk" value={head.at_risk ?? 0}
                 accent={(head.at_risk || 0) > 0 ? "red" : null}
                 icon={<ShieldAlert className="w-4 h-4 text-red-400" />} />
        <KpiCard label="Anomalies" value={head.anomalies_flagged ?? 0}
                 accent={(head.anomalies_flagged || 0) > 0 ? "amber" : null}
                 icon={<AlertTriangle className="w-4 h-4 text-amber-400" />} />
        <KpiCard label="Recommendations" value={head.recommendations ?? 0}
                 icon={<Lightbulb className="w-4 h-4 text-yellow-400" />} />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-yellow-400" />Top recommendations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {recs.length === 0 && (
              <p className="text-sm text-muted-foreground">No high-signal recommendations right now — the platform is healthy.</p>
            )}
            {recs.map((r) => <RecCard key={r.id} rec={r} compact />)}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />Top anomalies
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {anomalies.length === 0 && (
              <p className="text-sm text-muted-foreground">No anomalies detected yesterday.</p>
            )}
            {anomalies.map((a) => <AnomalyRow key={a.key} a={a} />)}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ── retention ─────────────────────────────────────────────────────────

function RetentionPanel({ data }) {
  if (!data || data.empty) return <EmptyCard>No retention data — run an aggregation.</EmptyCard>;
  const buckets = data.buckets || [];
  const total = data.total_users || 0;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Avg retention score" value={data.avg ?? 0} hint="0–100"
                 icon={<Heart className="w-4 h-4 text-pink-400" />} />
        {buckets.map((b) => (
          <KpiCard key={b.label} label={b.label} value={b.count}
                   hint={`${total > 0 ? Math.round((b.count / total) * 100) : 0}%`}
                   icon={<Activity className="w-4 h-4 text-primary" />} />
        ))}
      </div>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Retention by tier</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {Object.entries(data.by_tier || {}).map(([tier, v]) => (
            <div key={tier} className="flex items-center gap-3">
              <Badge variant="outline" className="w-24 justify-center uppercase">{tier}</Badge>
              <div className="flex-1"><ScoreBar score={v.avg} accent={tier === "founding" ? "amber" : tier === "premium" ? "emerald" : "primary"} /></div>
              <div className="w-20 text-right text-xs tabular-nums">{v.avg} · n={v.count}</div>
            </div>
          ))}
        </CardContent>
      </Card>
      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Top engaged</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {(data.top_engaged || []).slice(0, 8).map((u) => <UserScoreRow key={u.user_id} u={u} accent="emerald" />)}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Falling engagement</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {(data.falling_engagement || []).length === 0
              ? <p className="text-sm text-muted-foreground">No declining users right now.</p>
              : (data.falling_engagement || []).slice(0, 8).map((u) => <UserScoreRow key={u.user_id} u={u} accent="red" />)}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function UserScoreRow({ u, accent }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="flex-1 truncate">
        <span className="font-medium">{u.name || u.email || u.user_id?.slice(0, 6)}</span>
        {u.premium && <Crown className="inline w-3 h-3 text-amber-400 ml-1" />}
      </span>
      <span className="w-28"><ScoreBar score={u.score} accent={accent} /></span>
      <span className="w-12 text-right text-xs text-muted-foreground tabular-nums">{u.score}</span>
      {u.trend && <TrendArrow trend={u.trend} />}
    </div>
  );
}

// ── conversion ─────────────────────────────────────────────────────────

function ConversionPanel({ data }) {
  if (!data || data.empty) return <EmptyCard>No conversion data yet.</EmptyCard>;
  const drivers = data.drivers || [];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Premium" value={data.premium_count ?? 0}
                 hint={`${data.premium_pct ?? 0}% of ${data.total_users ?? 0}`}
                 icon={<Crown className="w-4 h-4 text-amber-400" />} />
        <KpiCard label="Founders" value={data.founding_count ?? 0}
                 icon={<Sparkles className="w-4 h-4 text-amber-400" />} />
        <KpiCard label="Duo couples" value={data.duo_count ?? 0}
                 icon={<Heart className="w-4 h-4 text-pink-400" />} />
        <KpiCard label="Paywall views" value={data.paywall_views_total ?? 0}
                 hint={`${data.paywall_to_premium_ratio ?? 0}% conversion`}
                 icon={<Target className="w-4 h-4 text-primary" />} />
      </div>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Pre-purchase behaviour drivers</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {drivers.map((d) => (
            <div key={d.label} className="flex items-center gap-3">
              <div className="flex-1 truncate text-sm font-medium">{d.label}</div>
              <div className="flex-1"><ScoreBar score={Math.min(100, d.ratio * 25)} accent="emerald" /></div>
              <div className="w-44 text-right text-xs tabular-nums text-muted-foreground">
                premium {d.premium_avg} · free {d.free_avg} ·
                <span className="text-foreground font-semibold"> {d.ratio}× </span>
              </div>
            </div>
          ))}
          {drivers.length === 0 && <p className="text-sm text-muted-foreground">No driver data yet.</p>}
        </CardContent>
      </Card>
    </div>
  );
}

// ── churn risk ─────────────────────────────────────────────────────────

const ACTION_ICONS = {
  push: Bell, in_app: MessageSquare, email: Mail,
  promo: Gift, trial: Sparkles, win_back: Send,
};
const REAL_ACTIONS = new Set(["push", "in_app", "email", "promo", "trial", "win_back"]);

function ChurnRiskPanel({ data, onAction }) {
  if (!data || data.empty) return <EmptyCard>No churn data — run an aggregation.</EmptyCard>;
  const lvl = data.by_level || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Critical risk" value={lvl.critical || 0} accent={lvl.critical ? "red" : null}
                 icon={<ShieldAlert className="w-4 h-4 text-red-500" />} />
        <KpiCard label="High risk" value={lvl.high || 0} accent={lvl.high ? "red" : null}
                 icon={<AlertTriangle className="w-4 h-4 text-red-400" />} />
        <KpiCard label="Medium risk" value={lvl.medium || 0} accent={lvl.medium ? "amber" : null}
                 icon={<AlertTriangle className="w-4 h-4 text-amber-400" />} />
        <KpiCard label="Healthy" value={lvl.low || 0}
                 icon={<Heart className="w-4 h-4 text-emerald-400" />} />
      </div>
      {(data.premium_at_risk || []).length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Crown className="w-4 h-4 text-amber-400" />
              {(data.premium_at_risk || []).length} premium users at risk
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {(data.premium_at_risk || []).slice(0, 6).map((u) => (
              <ChurnUserRow key={u.user_id} u={u} onAction={onAction} />
            ))}
          </CardContent>
        </Card>
      )}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Top at-risk users</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(data.at_risk || []).length === 0 && (
            <p className="text-sm text-muted-foreground">Nobody at risk right now 🎉</p>
          )}
          {(data.at_risk || []).slice(0, 12).map((u) => (
            <ChurnUserRow key={u.user_id} u={u} onAction={onAction} />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function ChurnUserRow({ u, onAction }) {
  const [expanded, setExpanded] = useState(false);
  const tint = u.level === "critical" ? "border-red-600/50 bg-red-500/5"
    : u.level === "high" ? "border-red-500/40 bg-red-500/5"
    : "border-amber-500/30 bg-amber-500/5";
  return (
    <div className={`border rounded-lg p-2.5 ${tint}`} data-testid={`churn-row-${u.user_id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium truncate">{u.name || u.email || u.user_id.slice(0, 6)}</span>
            {u.premium && <Crown className="w-3.5 h-3.5 text-amber-400" />}
            <SeverityBadge severity={u.level} />
            {u.days_inactive !== undefined && u.days_inactive !== null && (
              <span className="text-[11px] text-muted-foreground">
                <Clock className="inline w-3 h-3 mr-0.5" /> {u.days_inactive}d inactive
              </span>
            )}
          </div>
        </div>
        <div className="w-28"><ScoreBar score={u.score} accent="red" /></div>
        <span className="w-10 text-right text-xs tabular-nums">{u.score}</span>
        <button
          onClick={() => setExpanded((e) => !e)}
          className="text-xs text-primary hover:underline"
          data-testid={`churn-expand-${u.user_id}`}
        >
          {expanded ? "Hide" : "Why?"}
        </button>
      </div>
      {expanded && (
        <div className="mt-2 space-y-1.5">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Contributing factors</div>
          {(u.factors || []).filter((f) => f.contribution > 0).slice(0, 6).map((f) => (
            <div key={f.label} className="flex items-center gap-2 text-xs">
              <span className="flex-1 truncate">{f.label}</span>
              <span className="text-muted-foreground">{String(f.value)}</span>
              <span className="w-24"><ScoreBar score={f.contribution * 5} accent="red" /></span>
              <span className="w-10 text-right text-muted-foreground tabular-nums">{f.contribution}</span>
            </div>
          ))}
          <div className="flex flex-wrap gap-2 pt-1.5">
            {["push", "in_app", "email", "promo", "trial", "win_back"].map((act) => {
              const Icon = ACTION_ICONS[act] || Send;
              const real = REAL_ACTIONS.has(act);
              return (
                <Button
                  key={act} size="sm"
                  variant={real ? "outline" : "ghost"}
                  className={real ? "" : "opacity-60"}
                  onClick={() => onAction(u, act)}
                  data-testid={`churn-action-${act}-${u.user_id}`}
                >
                  <Icon className="w-3.5 h-3.5 mr-1" />
                  {act.replace("_", " ")}
                  {!real && <Lock className="w-3 h-3 ml-1" />}
                </Button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── feature insights ──────────────────────────────────────────────────

function FeatureInsightsPanel({ data }) {
  if (!data || data.empty) return <EmptyCard>No feature insights yet.</EmptyCard>;
  const baseline = data.baseline || {};
  const features = data.features || [];
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Baseline</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {baseline.total_users || 0} users · premium {baseline.premium_pct ?? 0}% · avg streak {baseline.avg_streak ?? 0}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Feature → premium / retention correlation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2.5">
          {features.map((f) => (
            <div key={f.feature} className="space-y-1" data-testid={`feature-row-${f.feature}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-sm">{f.feature.replace(/_/g, " ")}</span>
                <Badge variant="outline" className="text-[10px]">adoption {f.adoption_pct}%</Badge>
                <span className="text-xs text-muted-foreground">
                  premium <span className="text-emerald-400 font-semibold">{f.premium_pct}%</span> ({f.premium_lift >= 0 ? "+" : ""}{f.premium_lift}pp)
                </span>
                <span className="text-xs text-muted-foreground">
                  avg streak <span className="text-foreground font-semibold">{f.avg_streak}</span>
                </span>
                <span className="ml-auto text-xs tabular-nums">corr {f.correlation}</span>
              </div>
              <ScoreBar score={f.correlation}
                        accent={f.correlation >= 75 ? "emerald" : f.correlation >= 50 ? "primary" : "amber"} />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ── experiment insights ───────────────────────────────────────────────

function ExperimentInsightsPanel({ data }) {
  if (!data || data.empty) return <EmptyCard>No experiment data.</EmptyCard>;
  const exps = data.experiments || [];
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <KpiCard label="Total" value={exps.length}
                 icon={<FlaskConical className="w-4 h-4 text-pink-400" />} />
        <KpiCard label="Running" value={data.running_count || 0}
                 icon={<Activity className="w-4 h-4 text-emerald-400" />} />
        <KpiCard label="Promising" value={data.promising_count || 0}
                 icon={<Sparkles className="w-4 h-4 text-amber-400" />} />
      </div>
      {exps.length === 0 && <EmptyCard>No experiments yet.</EmptyCard>}
      {exps.map((e) => (
        <Card key={e.key} data-testid={`exp-insight-${e.key}`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="text-base flex items-center gap-2">
                <FlaskConical className="w-4 h-4 text-pink-400" />
                {e.name || e.key}
                <code className="text-[10px] font-mono text-muted-foreground">{e.key}</code>
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant={e.status === "running" ? "default" : "outline"}>{e.status}</Badge>
                <ConfidenceBadge confidence={e.confidence} />
                {e.winner && <Badge className="bg-amber-500 hover:bg-amber-500">★ {e.winner}</Badge>}
              </div>
            </div>
            <p className="text-sm text-foreground/80 mt-1">{e.recommendation}</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {(e.variants || []).map((v) => (
                <div key={v.variant} className="flex items-center gap-3 text-sm">
                  <div className="w-24 truncate">{v.variant}{e.winner === v.variant && <span className="ml-1 text-amber-400">★</span>}</div>
                  <div className="flex-1"><ScoreBar score={v.rate * 100 * 4} accent={e.winner === v.variant ? "amber" : "primary"} /></div>
                  <div className="w-44 text-right text-xs text-muted-foreground tabular-nums">
                    {v.conversions}/{v.impressions} · <span className="text-foreground font-semibold">{(v.rate * 100).toFixed(1)}%</span>
                  </div>
                </div>
              ))}
              {(e.variants || []).length === 0 && (
                <p className="text-sm text-muted-foreground">No traffic yet.</p>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── recommendations ───────────────────────────────────────────────────

function RecCard({ rec, compact = false }) {
  return (
    <div className="border border-border rounded-lg p-3 bg-card/50" data-testid={`rec-${rec.id}`}>
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <ImpactBadge impact={rec.impact} />
        <span className="text-[10px] uppercase text-muted-foreground">P{rec.priority}</span>
        {rec.metric && <span className="ml-auto text-[11px] text-muted-foreground">{rec.metric}</span>}
      </div>
      <h4 className="font-semibold text-sm">{rec.title}</h4>
      {!compact && rec.body && (
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{rec.body}</p>
      )}
      {rec.action && (
        <div className="mt-2 text-xs text-primary flex items-center gap-1">
          <ArrowRight className="w-3 h-3" /> {rec.action}
        </div>
      )}
    </div>
  );
}

function RecommendationsPanel({ data }) {
  const recs = (data && !data.empty) ? (data.recommendations || []) : [];
  const grouped = useMemo(() => {
    const map = { revenue: [], retention: [], activation: [] };
    recs.forEach((r) => { (map[r.impact] = map[r.impact] || []).push(r); });
    return map;
  }, [recs]);
  if (!data || data.empty) return <EmptyCard>No recommendations — system is stable.</EmptyCard>;
  if (recs.length === 0) return <EmptyCard>No high-signal recommendations right now.</EmptyCard>;
  return (
    <div className="space-y-4">
      {["revenue", "retention", "activation"].map((impact) => (
        (grouped[impact] || []).length > 0 && (
          <div key={impact}>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-2">
              <ImpactBadge impact={impact} />
              <span>{(grouped[impact] || []).length} insight{(grouped[impact] || []).length === 1 ? "" : "s"}</span>
            </h3>
            <div className="grid lg:grid-cols-2 gap-3">
              {(grouped[impact] || []).map((r) => <RecCard key={r.id} rec={r} />)}
            </div>
          </div>
        )
      ))}
    </div>
  );
}

// ── anomalies ─────────────────────────────────────────────────────────

function AnomalyRow({ a }) {
  const Icon = a.direction === "spike" ? TrendingUp
    : a.direction === "drop" ? TrendingDown : Minus;
  const tint = a.severity === "critical" ? "text-red-400"
    : a.severity === "high" ? "text-red-400"
    : a.severity === "medium" ? "text-amber-400" : "text-emerald-400";
  return (
    <div className="border border-border rounded-lg p-2.5 bg-card/50" data-testid={`anomaly-${a.key}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Icon className={`w-4 h-4 ${tint}`} />
        <span className="font-semibold text-sm">{a.label}</span>
        <Badge variant="outline" className="text-[10px]">{a.system}</Badge>
        <SeverityBadge severity={a.severity} />
        <span className="ml-auto text-xs tabular-nums text-muted-foreground">
          {a.yesterday} <span className="text-[10px]">vs</span> avg {a.avg} · z={a.z_score} · {a.pct_change > 0 ? "+" : ""}{a.pct_change}%
        </span>
      </div>
      {(a.probable_causes || []).length > 0 && (
        <ul className="mt-1 ml-6 list-disc text-[11px] text-muted-foreground space-y-0.5">
          {(a.probable_causes || []).slice(0, 4).map((c, i) => <li key={i}>{c}</li>)}
        </ul>
      )}
    </div>
  );
}

function AnomaliesPanel({ data }) {
  if (!data || data.empty) return <EmptyCard>No anomaly data yet.</EmptyCard>;
  const items = data.anomalies || [];
  const flagged = items.filter((a) => ["critical", "high", "medium"].includes(a.severity));
  const normal = items.filter((a) => a.severity === "normal");
  const insufficient = items.filter((a) => a.severity === "insufficient_data");
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            {flagged.length} flagged metric{flagged.length === 1 ? "" : "s"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {flagged.length === 0 && <p className="text-sm text-muted-foreground">All clear — no metric is more than 1.4σ from its baseline.</p>}
          {flagged.map((a) => <AnomalyRow key={a.key} a={a} />)}
        </CardContent>
      </Card>
      {(normal.length + insufficient.length) > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-muted-foreground text-sm">Other metrics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {normal.map((a) => <AnomalyRow key={a.key} a={a} />)}
            {insufficient.map((a) => (
              <div key={a.key} className="text-xs text-muted-foreground">
                {a.label} — insufficient data
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── main ──────────────────────────────────────────────────────────────

export default function AdminAIInsightsTab() {
  const [overview, setOverview] = useState(null);
  const [retention, setRetention] = useState(null);
  const [conversion, setConversion] = useState(null);
  const [churn, setChurn] = useState(null);
  const [features, setFeatures] = useState(null);
  const [experiments, setExperiments] = useState(null);
  const [recs, setRecs] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastComputed, setLastComputed] = useState(null);
  const [stubDialog, setStubDialog] = useState(null);

  const fetchAll = useCallback(async () => {
    const safe = (p) => p.then((r) => r.data).catch(() => ({ empty: true }));
    setLoading(true);
    const [o, r, c, ch, f, e, rec, a] = await Promise.all([
      safe(axios.get(`${API}/admin/ai/overview`)),
      safe(axios.get(`${API}/admin/ai/retention`)),
      safe(axios.get(`${API}/admin/ai/conversion`)),
      safe(axios.get(`${API}/admin/ai/churn-risk`)),
      safe(axios.get(`${API}/admin/ai/feature-insights`)),
      safe(axios.get(`${API}/admin/ai/experiment-insights`)),
      safe(axios.get(`${API}/admin/ai/recommendations`)),
      safe(axios.get(`${API}/admin/ai/anomalies`)),
    ]);
    setOverview(o); setRetention(r); setConversion(c); setChurn(ch);
    setFeatures(f); setExperiments(e); setRecs(rec); setAnomalies(a);
    setLastComputed(o?.last_computed || o?.computed_at || null);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await axios.post(`${API}/admin/ai/refresh`);
      if (r.data?.ok) {
        toast.success(`Recomputed in ${(r.data.duration_ms / 1000).toFixed(1)}s`);
        await fetchAll();
      } else if (r.data?.reason === "rate_limited") {
        toast(`⏳ ${r.data.message}`);
      } else if (r.data?.reason === "locked") {
        toast(`🔒 ${r.data.message}`);
      } else {
        toast.error(r.data?.message || "Refresh failed");
      }
    } catch (e) {
      toast.error("Refresh failed");
    } finally { setRefreshing(false); }
  };

  const onChurnAction = async (user, action) => {
    if (!REAL_ACTIONS.has(action)) {
      setStubDialog({ user, action });
      return;
    }
    try {
      const r = await axios.post(`${API}/admin/ai/churn-action`, {
        user_id: user.user_id, action,
      });
      if (r.data?.ok) {
        toast.success(`Sent ${action.replace("_", " ")} to ${user.name || user.email}`);
      } else if (r.data?.stub) {
        setStubDialog({ user, action, message: r.data.message });
      } else {
        toast.error(r.data?.message || "Action failed");
      }
    } catch (e) {
      toast.error("Action failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-ai-insights">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold">AI Insights</h2>
          <span className="text-xs text-muted-foreground">
            Last computed: {lastComputed ? timeAgo(lastComputed) : "never"}
          </span>
        </div>
        <Button
          size="sm" variant="outline"
          onClick={onRefresh} disabled={refreshing || loading}
          data-testid="ai-refresh-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Recomputing…" : "Recompute now"}
        </Button>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <div className="overflow-x-auto -mx-4 px-4">
          <TabsList className="bg-card border border-border inline-flex min-w-max">
            <TabsTrigger value="overview" data-testid="ai-tab-overview"><Activity className="w-3.5 h-3.5 mr-1" />Overview</TabsTrigger>
            <TabsTrigger value="retention" data-testid="ai-tab-retention"><Heart className="w-3.5 h-3.5 mr-1" />Retention</TabsTrigger>
            <TabsTrigger value="conversion" data-testid="ai-tab-conversion"><Target className="w-3.5 h-3.5 mr-1" />Conversion</TabsTrigger>
            <TabsTrigger value="churn" data-testid="ai-tab-churn"><ShieldAlert className="w-3.5 h-3.5 mr-1" />Churn Risk</TabsTrigger>
            <TabsTrigger value="features" data-testid="ai-tab-features"><Zap className="w-3.5 h-3.5 mr-1" />Feature Insights</TabsTrigger>
            <TabsTrigger value="experiments" data-testid="ai-tab-experiments"><FlaskConical className="w-3.5 h-3.5 mr-1" />Experiment Insights</TabsTrigger>
            <TabsTrigger value="recs" data-testid="ai-tab-recs"><Lightbulb className="w-3.5 h-3.5 mr-1" />Recommendations</TabsTrigger>
            <TabsTrigger value="anomalies" data-testid="ai-tab-anomalies"><AlertTriangle className="w-3.5 h-3.5 mr-1" />Anomalies</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="overview"><OverviewPanel data={overview} /></TabsContent>
        <TabsContent value="retention"><RetentionPanel data={retention} /></TabsContent>
        <TabsContent value="conversion"><ConversionPanel data={conversion} /></TabsContent>
        <TabsContent value="churn"><ChurnRiskPanel data={churn} onAction={onChurnAction} /></TabsContent>
        <TabsContent value="features"><FeatureInsightsPanel data={features} /></TabsContent>
        <TabsContent value="experiments"><ExperimentInsightsPanel data={experiments} /></TabsContent>
        <TabsContent value="recs"><RecommendationsPanel data={recs} /></TabsContent>
        <TabsContent value="anomalies"><AnomaliesPanel data={anomalies} /></TabsContent>
      </Tabs>

      {/* Stub action dialog */}
      <Dialog open={Boolean(stubDialog)} onOpenChange={(o) => !o && setStubDialog(null)}>
        <DialogContent data-testid="stub-action-dialog">
          <DialogHeader>
            <DialogTitle>Coming soon</DialogTitle>
            <DialogDescription>
              {stubDialog?.message || `${stubDialog?.action?.replace("_", " ")} delivery is coming soon. Push and in-app notifications work today.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setStubDialog(null)}>Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
